import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { useState, useRef, useLayoutEffect, useCallback, useEffect, useMemo, useContext } from 'react';
import { createPortal } from 'react-dom';
import { useFocusAndReturn } from '../dialog-primitive/dialog-primitive.hooks.js';
import { useTabLoop } from '../hooks/use-tab-loop.js';
import { accept } from '../utils/event.js';
import { getMovementType, LastInteraction } from '../utils/interaction.js';
import { PopoverOutletContext } from '../utils/popover-outlet.js';
import { useSubMenuContainer, useProgrammaticMenuOpen, useMenuPositioner, useMenuNav, useMenuTypeahead, MenuInternalContext } from './menu-v2.hooks.js';
import { Popup } from './menu-v2.popup.js';
import { ON_CLOSE_DELAY_MS, backdrop } from './menu-v2.css.js';

// Important: if this is no longer static, then use useMemo() so it has a stable
// reference and a positioner is not recreated every render.
const DEFAULT_POSITION_MARGIN = { y: 4 };
const DEFAULT_POSITION = {
    area: 'bottom span-right',
    margin: DEFAULT_POSITION_MARGIN,
};
/**
 * Menu Root
 *
 * @compose { "root": true, "strictChildren": true }
 */
function Root({ children, manager, portalTargetOverride, position, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, ...props }) {
    const { registerOpenSubMenu } = useSubMenuContainer();
    const programmaticOpen = useProgrammaticMenuOpen(manager);
    const [interactionMode, setInteractionMode] = useState(getMovementType);
    const isClosing = useRef(false);
    // The trigger sits outside the popup handlers, so reset the mode when a new menu session opens.
    useLayoutEffect(() => {
        if (manager.open) {
            isClosing.current = false;
            setInteractionMode(LastInteraction.type);
        }
    }, [manager.open]);
    const onCloseRootTimeout = useRef(0);
    const onCloseRoot = useCallback(() => {
        isClosing.current = true;
        window.clearTimeout(onCloseRootTimeout.current);
        onCloseRootTimeout.current = window.setTimeout(() => {
            manager.setOpen(false);
        }, ON_CLOSE_DELAY_MS);
        return () => window.clearTimeout(onCloseRootTimeout.current);
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps -- this is sufficient
    [manager.setOpen]);
    // Clear any pending close timer on unmount so it can’t fire after teardown
    useEffect(() => () => window.clearTimeout(onCloseRootTimeout.current), []);
    // used to determine if an “up” event on the backdrop should close the menu
    const hasEnteredMenu = useRef(false);
    const portalTarget = usePopoverOutletOrOverride(portalTargetOverride);
    const menuInternalContext = useMemo(() => ({
        interactionMode,
        registerOpenSubMenu,
        onCloseRoot,
        isClosing,
        portalTarget,
        isClickHold: manager.isClickHold,
        programmaticOpen,
    }), [
        interactionMode,
        isClosing,
        manager.isClickHold,
        onCloseRoot,
        portalTarget,
        programmaticOpen,
        registerOpenSubMenu,
    ]);
    const handleClose = () => {
        isClosing.current = true;
        registerOpenSubMenu(undefined);
        programmaticOpen.endProgrammaticOpen();
        manager.setOpen(false);
    };
    const resolvedPosition = useMemo(() => {
        return position ? { ...DEFAULT_POSITION, ...position } : DEFAULT_POSITION;
    }, [position]);
    const popupRef = useRef(null);
    const positioner = useMenuPositioner({ position: resolvedPosition });
    const menuNav = useMenuNav({ popupRef });
    const typeahead = useMenuTypeahead({ popupRef });
    const focusAndReturn = useFocusAndReturn({ ref: popupRef }, { open: manager.open });
    const tabLoop = useTabLoop();
    const hasExplicitLabel = ariaLabel !== undefined || ariaLabelledBy !== undefined;
    if (!manager.open) {
        hasEnteredMenu.current = false;
        return null;
    }
    return createPortal(jsxs(Fragment, { children: [jsx("div", { role: "none", className: backdrop, 
                // Note: onClick fires for touch devices as soon as the trigger is
                // tapped and released (on touch devices, it doesn’t need a “down”
                // event). For that reason, don’t use onClick.
                onPointerDown: handleClose, onPointerUp: (e) => {
                    if (e.pointerType === 'mouse' && hasEnteredMenu.current && manager.isClickHold.current) {
                        handleClose();
                    }
                } }), jsx(MenuInternalContext.Provider, { value: menuInternalContext, children: jsx(Popup, { ...props, "aria-modal": true, "aria-label": ariaLabel, "aria-labelledby": hasExplicitLabel ? ariaLabelledBy : manager.private.triggerId, id: manager.id, ref: focusAndReturn.ref, anchorRef: manager.anchorRef, open: manager.open, positioner: positioner, onPointerEnter: () => (hasEnteredMenu.current = true), onPointerMove: () => {
                        hasEnteredMenu.current = true;
                        setInteractionMode(getMovementType());
                    }, onPointerDownCapture: programmaticOpen.endProgrammaticOpen, onKeyDownCapture: (e) => {
                        setInteractionMode('keyboard');
                        programmaticOpen.endProgrammaticOpen();
                        typeahead.onKeyDownCapture(e);
                    }, onKeyDown: (e) => {
                        // In general, propagating events outside Menu causes more problems
                        // than other components.
                        e.stopPropagation();
                        // global close menu
                        if (e.key === 'Escape') {
                            handleClose();
                            accept(e);
                        }
                        // If space wasn’t handled by children (including typeahead), then
                        // prevent it from bubbling. This prevents space from scrolling
                        // the page while menu is open.
                        else if (e.key === ' ') {
                            accept(e);
                        }
                        tabLoop.onKeyDown(e);
                        menuNav.popupProps.onKeyDown(e);
                    }, onBlur: (e) => {
                        tabLoop.onBlur(e);
                        focusAndReturn.onBlur(e);
                    }, onPointerUp: () => {
                        // Note: some pointerup events may “slip through the cracks” in
                        // between other elements, such as separators and/or other
                        // unintended targets. This is just a generic catchall that’s
                        // needed in case an unexpected child doesn’t handle pointerup
                        // properly.
                        if (manager.isClickHold.current)
                            onCloseRoot();
                    }, children: children }) })] }), portalTarget);
}
Root.displayName = 'MenuV2.Root';
/**
 * Abstraction to determine portal target.
 *
 * Because of how React works, this hook will fire the very first time before
 * any elements have mounted. So we don’t know yet if we should listen to either
 * portalTargetOverride, popoverOutletContext, or our internal fallback, until
 * the first rerender post-mount. So we set an element ref in state, and use a
 * useLayoutEffect to verify we’re portaling to the right target.
 */
function usePopoverOutletOrOverride(override) {
    const popoverOutlet = useContext(PopoverOutletContext)?.current;
    const [renderedTarget, setRenderedTarget] = useState(null);
    useLayoutEffect(() => {
        const newTarget = override || popoverOutlet;
        if (newTarget && newTarget !== renderedTarget)
            setRenderedTarget(newTarget);
    }, [override, renderedTarget, popoverOutlet]);
    return renderedTarget || document.body; // fallback
}

export { Root };
//# sourceMappingURL=menu-v2.root.js.map
